const cleanText = (value) => {
  if (value === undefined || value === null) return null;
  const text = String(value).trim();
  return text || null;
};

const normalizeText = (value) =>
  String(value || "")
    .trim()
    .toLowerCase()
    .replace(/[_-]+/g, " ")
    .replace(/\s+/g, " ");

const toNumber = (value) => {
  if (value === undefined || value === null || value === "") return null;
  const number = Number(value);
  return Number.isFinite(number) ? number : null;
};

const parseDate = (value) => {
  if (!value) return null;
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? null : date;
};

const makeCoordinates = (latitude, longitude, source = "unknown") => {
  const lat = toNumber(latitude);
  const lng = toNumber(longitude);

  if (
    lat === null ||
    lng === null ||
    lat < -90 ||
    lat > 90 ||
    lng < -180 ||
    lng > 180
  ) {
    return null;
  }

  return {
    latitude: lat,
    longitude: lng,
    source,
  };
};

const coordinatesFromArray = (value, source) => {
  if (!Array.isArray(value) || value.length < 2) return null;

  // GeoJSON uses [longitude, latitude].
  return makeCoordinates(value[1], value[0], source);
};

export const getRawShipment = (shipment) =>
  shipment?.rawResponse?.details?.shipment ||
  shipment?.rawResponse?.details?.data?.shipment ||
  shipment?.rawResponse?.shipment ||
  shipment?.rawResponse?.data?.shipment ||
  shipment?.rawResponse?.details?.data ||
  shipment?.rawResponse?.data ||
  {};

export const getRawGeoJson = (shipment) =>
  shipment?.rawResponse?.geojson?.geojson ||
  shipment?.rawResponse?.geojson ||
  shipment?.geojson ||
  null;

export const getRoute = (shipment) => getRawShipment(shipment)?.route || {};

export const getMovements = (shipment) => {
  const rawShipment = getRawShipment(shipment);
  const containers = Array.isArray(rawShipment?.containers)
    ? rawShipment.containers
    : [];

  return containers.flatMap((container) => {
    const movements =
      container?.movements ||
      container?.events ||
      container?.tracking_events ||
      container?.trackingEvents ||
      [];

    return Array.isArray(movements) ? movements : [];
  });
};

const getEventDate = (event) =>
  event?.timestamp ||
  event?.date ||
  event?.event_date ||
  event?.eventDate ||
  event?.actual_date ||
  event?.actualDate ||
  event?.planned_date ||
  event?.plannedDate ||
  event?.estimated_date ||
  event?.estimatedDate ||
  event?.time ||
  null;

const getEventName = (event) =>
  cleanText(
    event?.description ||
      event?.event ||
      event?.status ||
      event?.type ||
      event?.code ||
      event?.name
  );

const isEstimatedEvent = (event) => {
  const text = normalizeText(
    event?.actual ||
      event?.is_actual ||
      event?.isActual ||
      event?.event_type ||
      event?.eventType ||
      event?.date_type ||
      event?.dateType ||
      ""
  );

  return (
    text.includes("estimated") ||
    text.includes("planned") ||
    text.includes("scheduled")
  );
};

export const getLastEvent = (shipment) => {
  if (shipment?.lastEvent) {
    return {
      name: shipment.lastEvent,
      date: shipment.lastEventDate || null,
      raw: null,
    };
  }

  const now = Date.now();
  const events = getMovements(shipment)
    .map((event) => ({
      event,
      name: getEventName(event),
      date: parseDate(getEventDate(event)),
    }))
    .filter((item) => item.name || item.date);

  if (!events.length) {
    return {
      name: "No event available",
      date: null,
      raw: null,
    };
  }

  const actualPastEvents = events.filter(
    (item) =>
      !isEstimatedEvent(item.event) &&
      item.date &&
      item.date.getTime() <= now + 24 * 60 * 60 * 1000
  );

  const source = actualPastEvents.length ? actualPastEvents : events;

  source.sort((a, b) => {
    const aTime = a.date?.getTime() || 0;
    const bTime = b.date?.getTime() || 0;
    return aTime - bTime;
  });

  const last = source[source.length - 1];

  return {
    name: last.name || "No event available",
    date: last.date || null,
    raw: last.event || null,
  };
};

const buildCombinedStatusText = (shipment) => {
  const rawShipment = getRawShipment(shipment);
  const lastEvent = getLastEvent(shipment);

  return normalizeText(
    [
      shipment?.statusText,
      shipment?.status,
      rawShipment?.status,
      rawShipment?.status_text,
      rawShipment?.statusText,
      rawShipment?.shipment_status,
      rawShipment?.shipmentStatus,
      lastEvent.name,
    ]
      .filter(Boolean)
      .join(" ")
  );
};

const STATUS_RULES = [
  {
    key: "delivered",
    label: "Delivered",
    group: "delivered",
    progress: 100,
    terms: [
      "delivered",
      "delivery completed",
      "delivery complete",
      "empty returned",
      "empty return",
      "empty depot",
      "completed",
    ],
  },
  {
    key: "gate_out",
    label: "Gate Out",
    group: "arrived",
    progress: 95,
    terms: ["gate out", "out gate", "gateout"],
  },
  {
    key: "not_released",
    label: "Not Released",
    group: "arrived",
    progress: 88,
    terms: ["not released", "not release", "hold for release"],
  },
  {
    key: "customs_cleared",
    label: "Customs Cleared",
    group: "arrived",
    progress: 91,
    terms: ["customs cleared", "custom cleared"],
  },
  {
    key: "released",
    label: "Released",
    group: "arrived",
    progress: 92,
    terms: ["released", "cargo release", "release confirmed"],
  },
  {
    key: "discharged",
    label: "Discharged",
    group: "arrived",
    progress: 84,
    terms: ["discharged", "discharge", "unloaded", "disc"],
  },
  {
    key: "arrived",
    label: "Arrived at Port",
    group: "arrived",
    progress: 76,
    terms: ["arrived", "arrival", "arrv", "port arrival"],
  },
  {
    key: "sailing",
    label: "In Transit",
    group: "inTransit",
    progress: 55,
    terms: [
      "sailing",
      "in transit",
      "inprogress",
      "in progress",
      "departed",
      "departure",
      "vessel departed",
      "transshipment",
      "transit",
    ],
  },
  {
    key: "loaded",
    label: "Loaded on Vessel",
    group: "inTransit",
    progress: 30,
    terms: ["loaded", "load on vessel", "laden on board", "on board"],
  },
  {
    key: "gate_in",
    label: "Gate In",
    group: "inTransit",
    progress: 18,
    terms: ["gate in", "gatein", "in gate"],
  },
  {
    key: "booked",
    label: "Booked",
    group: "inTransit",
    progress: 8,
    terms: ["booked", "booking confirmed", "new", "created"],
  },
];

export const getTrackingStatus = (shipment) => {
  const text = buildCombinedStatusText(shipment);
  const match = STATUS_RULES.find((rule) =>
    rule.terms.some((term) => text.includes(term))
  );

  if (match) {
    return {
      ...match,
      rawText: text,
    };
  }

  const fallback =
    cleanText(shipment?.statusText) ||
    cleanText(shipment?.status) ||
    "Tracking Started";

  return {
    key: "tracking_started",
    label: fallback,
    group: "inTransit",
    progress: 10,
    rawText: text,
  };
};

export const getRouteProgress = (shipment) => {
  const rawShipment = getRawShipment(shipment);
  const apiProgress =
    rawShipment?.route?.transit_percentage ??
    rawShipment?.route?.transitPercentage ??
    rawShipment?.transit_percentage ??
    rawShipment?.transitPercentage ??
    shipment?.transitPercentage ??
    shipment?.progress;

  const parsed = toNumber(apiProgress);

  if (parsed !== null) {
    return Math.min(100, Math.max(0, parsed));
  }

  return getTrackingStatus(shipment).progress;
};

const getCoordinatesFromObject = (value, source) => {
  if (!value || typeof value !== "object") return null;

  return (
    makeCoordinates(value.latitude, value.longitude, source) ||
    makeCoordinates(value.lat, value.lng, source) ||
    makeCoordinates(value.lat, value.lon, source) ||
    coordinatesFromArray(value.coordinates, source) ||
    getCoordinatesFromObject(value.location, source) ||
    getCoordinatesFromObject(value.position, source)
  );
};

const getFeatureDate = (feature) =>
  parseDate(
    feature?.properties?.timestamp ||
      feature?.properties?.date ||
      feature?.properties?.event_date ||
      feature?.properties?.eventDate ||
      feature?.properties?.actual_date ||
      feature?.properties?.actualDate ||
      feature?.properties?.updated_at ||
      feature?.properties?.updatedAt ||
      feature?.properties?.last_updated ||
      feature?.properties?.lastUpdated
  );

const coordinatesFromFeature = (feature, source) => {
  if (!feature) return null;

  if (feature?.geometry?.type === "Point") {
    return coordinatesFromArray(feature.geometry.coordinates, source);
  }

  return (
    getCoordinatesFromObject(feature?.properties?.current, source) ||
    getCoordinatesFromObject(feature?.properties?.current_position, source) ||
    getCoordinatesFromObject(feature?.properties?.currentPosition, source) ||
    getCoordinatesFromObject(feature?.properties?.position, source)
  );
};

export const getGeoJsonRoutePoints = (shipment) => {
  const geojson = getRawGeoJson(shipment);
  const features = Array.isArray(geojson?.features) ? geojson.features : [];

  const lineFeatures = features.filter(
    (feature) =>
      feature?.geometry?.type === "LineString" &&
      Array.isArray(feature?.geometry?.coordinates)
  );

  if (!lineFeatures.length) return [];

  const preferred =
    lineFeatures.find((feature) => {
      const text = normalizeText(
        feature?.properties?.status ||
          feature?.properties?.type ||
          feature?.properties?.name
      );

      return text.includes("current") || text.includes("route");
    }) ||
    [...lineFeatures].sort(
      (a, b) =>
        (b?.geometry?.coordinates?.length || 0) -
        (a?.geometry?.coordinates?.length || 0)
    )[0];

  return preferred.geometry.coordinates
    .map((coordinates) => coordinatesFromArray(coordinates, "ShipsGo route"))
    .filter(Boolean)
    .map((coordinates) => [coordinates.latitude, coordinates.longitude]);
};

export const getPortCoordinates = (portNode, source = "port") => {
  const location = portNode?.location || portNode || {};

  return (
    getCoordinatesFromObject(location, source) ||
    getCoordinatesFromObject(portNode, source)
  );
};

export const getOriginCoordinates = (shipment) =>
  getPortCoordinates(getRoute(shipment)?.port_of_loading, "origin port");

export const getDestinationCoordinates = (shipment) =>
  getPortCoordinates(getRoute(shipment)?.port_of_discharge, "destination port");

export const getExactShipCoordinates = (shipment) => {
  const stored = makeCoordinates(
    shipment?.latitude,
    shipment?.longitude,
    "ShipsGo latest position"
  );

  if (stored) return stored;

  const rawShipment = getRawShipment(shipment);
  const route = rawShipment?.route || {};

  const directCandidates = [
    rawShipment?.current_position,
    rawShipment?.currentPosition,
    rawShipment?.map_point,
    rawShipment?.mapPoint,
    rawShipment?.position,
    rawShipment?.vessel_position,
    rawShipment?.vesselPosition,
    rawShipment?.current_vessel,
    route?.current_position,
    route?.currentPosition,
    route?.map_point,
    route?.mapPoint,
    route?.position,
  ];

  for (const candidate of directCandidates) {
    const coordinates = getCoordinatesFromObject(
      candidate,
      "ShipsGo current position"
    );

    if (coordinates) return coordinates;
  }

  const geojson = getRawGeoJson(shipment);
  const features = Array.isArray(geojson?.features) ? geojson.features : [];

  const currentFeature = features.find((feature) => {
    const text = normalizeText(
      feature?.properties?.status ||
        feature?.properties?.type ||
        feature?.properties?.name
    );

    return (
      text === "current" ||
      text.includes("current position") ||
      text.includes("vessel") ||
      text.includes("ship")
    );
  });

  const currentCoordinates = coordinatesFromFeature(
    currentFeature,
    "ShipsGo GeoJSON current position"
  );

  if (currentCoordinates) return currentCoordinates;

  const newestPoint = features
    .filter((feature) => feature?.geometry?.type === "Point")
    .map((feature) => ({ feature, date: getFeatureDate(feature) }))
    .sort((a, b) => (b.date?.getTime() || 0) - (a.date?.getTime() || 0))[0];

  const newestPointCoordinates = coordinatesFromFeature(
    newestPoint?.feature,
    "ShipsGo GeoJSON latest point"
  );

  if (newestPointCoordinates) return newestPointCoordinates;

  const routePoints = getGeoJsonRoutePoints(shipment);

  if (routePoints.length) {
    const lastPoint = routePoints[routePoints.length - 1];
    return makeCoordinates(
      lastPoint[0],
      lastPoint[1],
      "ShipsGo GeoJSON route point"
    );
  }

  return null;
};

export const getMapPosition = (shipment) => {
  const exact = getExactShipCoordinates(shipment);
  if (exact) return { ...exact, isExact: true };

  const status = getTrackingStatus(shipment);
  const fallback =
    status.group === "arrived" || status.group === "delivered"
      ? getDestinationCoordinates(shipment) || getOriginCoordinates(shipment)
      : getOriginCoordinates(shipment) || getDestinationCoordinates(shipment);

  return fallback ? { ...fallback, isExact: false } : null;
};

const formatPort = (portNode) => {
  const location = portNode?.location || portNode || {};
  const name = cleanText(location?.name || portNode?.name);
  const code = cleanText(location?.code || portNode?.code);

  if (name && code) return `${name} (${code})`;
  return name || code || "-";
};

export const getOriginPort = (shipment) =>
  cleanText(shipment?.originPort) ||
  formatPort(getRoute(shipment)?.port_of_loading);

export const getDestinationPort = (shipment) =>
  cleanText(shipment?.destinationPort) ||
  formatPort(getRoute(shipment)?.port_of_discharge);

export const getEta = (shipment) => {
  if (shipment?.eta) return shipment.eta;

  const rawShipment = getRawShipment(shipment);
  const pod = getRoute(shipment)?.port_of_discharge || {};

  return (
    pod?.date_of_discharge ||
    pod?.date_of_discharge_initial ||
    pod?.eta ||
    rawShipment?.eta ||
    rawShipment?.estimated_arrival ||
    rawShipment?.estimatedArrival ||
    null
  );
};

export const getVesselName = (shipment) => {
  if (cleanText(shipment?.vesselName)) return shipment.vesselName;

  const rawShipment = getRawShipment(shipment);
  const route = rawShipment?.route || {};
  const vessel = route?.vessel || rawShipment?.vessel || rawShipment?.current_vessel;

  if (cleanText(vessel?.name)) return vessel.name;
  if (cleanText(rawShipment?.vessel_name)) return rawShipment.vessel_name;

  const movement = getMovements(shipment).find(
    (item) => cleanText(item?.vessel?.name) || cleanText(item?.vessel_name)
  );

  if (movement) {
    return movement?.vessel?.name || movement?.vessel_name || "-";
  }

  const geojson = getRawGeoJson(shipment);
  const features = Array.isArray(geojson?.features) ? geojson.features : [];
  const feature = features.find(
    (item) =>
      cleanText(item?.properties?.vessel?.name) ||
      cleanText(item?.properties?.vessel_name)
  );

  return (
    feature?.properties?.vessel?.name ||
    feature?.properties?.vessel_name ||
    "-"
  );
};

export const getVoyageNo = (shipment) => {
  if (cleanText(shipment?.voyageNo)) return shipment.voyageNo;

  const rawShipment = getRawShipment(shipment);
  const route = rawShipment?.route || {};

  const direct =
    route?.voyage ||
    route?.voyage_no ||
    rawShipment?.voyage ||
    rawShipment?.voyage_no;

  if (cleanText(direct)) return direct;

  const movement = getMovements(shipment).find(
    (item) => cleanText(item?.voyage) || cleanText(item?.voyage_no)
  );

  return movement?.voyage || movement?.voyage_no || "-";
};

export const getTrackingNo = (shipment) =>
  shipment?.containerNumber ||
  shipment?.blNumber ||
  shipment?.bookingNumber ||
  shipment?.requestId ||
  "-";

export const buildTrackingSummary = (rows) =>
  (Array.isArray(rows) ? rows : []).reduce(
    (summary, shipment) => {
      summary.total += 1;
      const status = getTrackingStatus(shipment);

      if (status.group === "delivered") summary.delivered += 1;
      else if (status.group === "arrived") summary.arrived += 1;
      else summary.inTransit += 1;

      return summary;
    },
    {
      total: 0,
      inTransit: 0,
      arrived: 0,
      delivered: 0,
    }
  );

export const getStatusClassName = (statusOrShipment) => {
  const status =
    typeof statusOrShipment === "object"
      ? getTrackingStatus(statusOrShipment)
      : { key: normalizeText(statusOrShipment), group: "" };

  if (status.group === "delivered" || status.key === "delivered") {
    return "border-emerald-200 bg-emerald-50 text-emerald-700 dark:border-emerald-900 dark:bg-emerald-950 dark:text-emerald-300";
  }

  if (
    status.key === "not_released" ||
    status.key === "gate_out" ||
    status.key === "released" ||
    status.key === "customs_cleared"
  ) {
    return "border-amber-200 bg-amber-50 text-amber-700 dark:border-amber-900 dark:bg-amber-950 dark:text-amber-300";
  }

  if (status.group === "arrived") {
    return "border-violet-200 bg-violet-50 text-violet-700 dark:border-violet-900 dark:bg-violet-950 dark:text-violet-300";
  }

  return "border-blue-200 bg-blue-50 text-blue-700 dark:border-blue-900 dark:bg-blue-950 dark:text-blue-300";
};
